import {InvoiceItemApiDto} from "../../api/model/rest";
import {none, option, Option} from "ts-option";
import JsonConverter from "../../api/helper/JsonConverter";
import {isDefined} from "../../utils/Utils";
import NumberUtils from "../../utils/NumberUtils";

const uuidv4 = require('uuid/v4');

export default class InvoiceItem {

	constructor(public readonly id: string,
		public readonly amount: number,
		public readonly description: string,
		public readonly discount: Option<number>,
		public readonly quantity: number,
		public readonly taxRate: number,
		public readonly unitPrice: number,
		public readonly accountCode: Option<string>,
		public readonly type: InvoiceItemType) {}

	static fromJson(json: InvoiceItemApiDto): InvoiceItem {
		return new InvoiceItem(json.id, json.amount, json.description, option(json.discount), json.quantity, json.taxRate,
			json.unitPrice, option(json.accountCode), InvoiceItemType[json.type]);
	}

	static createEmpty(vatRegistered: boolean, vatRate: Option<number>): InvoiceItem {
		return new InvoiceItem(
			uuidv4(),
			null,
			"",
			none,
			1,
			vatRegistered ? vatRate.getOrElse(0) : 0,
			null,
			none,
			InvoiceItemType.USER_DEFINED
		)
	}

	canBeSubmitted() {
		return isDefined(this.description) && isDefined(this.quantity) && isDefined(this.taxRate) && isDefined(this.unitPrice)
			&& this.accountCode.isDefined;
	}

	/**
	 * Returns item with new unit price and updated total amount.
	 * @param unitPrice
	 */
	withNewUnitPrice(unitPrice: number) {
		const amount = this.quantity * unitPrice;
		const amountWithVat = NumberUtils.increaseByPercent(amount, this.taxRate);
		return this.copy({unitPrice, amount: amountWithVat});
	}

	/**
	 * Returns item with new quantity and updated total amount.
	 * @param quantity
	 */
	withNewQuantity(quantity: number) {
		const amount = quantity * this.unitPrice;
		const amountWithVat = NumberUtils.increaseByPercent(amount, this.taxRate);
		return this.copy({quantity, amount: amountWithVat});
	}

	/**
	 * Returns item with new total amount and updated unit price.
	 * @param amount
	 */
	withNewAmount(amount: number) {
		const unitPrice = NumberUtils.getValueBeforeIncreasingByPercent(amount, this.taxRate) / this.quantity;
		return this.copy({unitPrice, amount});
	}

	toJson(): InvoiceItemApiDto {
		const json: InvoiceItemApiDto = {
			id: this.id,
			amount: this.amount,
			description: this.description,
			discount: this.discount.orNull,
			quantity: this.quantity,
			taxRate: this.taxRate,
			unitPrice: this.unitPrice,
			accountCode: this.accountCode.orNull,
			type: this.type
		};
		return JsonConverter.removeEmptyFields(json);
	}

	copy({
		id = this.id,
		amount = this.amount,
		description = this.description,
		discount = this.discount,
		quantity = this.quantity,
		taxRate = this.taxRate,
		unitPrice = this.unitPrice,
		accountCode = this.accountCode,
		type = this.type
	}) {
		return new InvoiceItem(id, amount, description, discount, quantity, taxRate, unitPrice, accountCode, type);
	}
}

export enum InvoiceItemType {
	LABOUR_AND_PARTS = "LABOUR_AND_PARTS",
	USER_DEFINED = "USER_DEFINED"
}
